import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLeads } from "@/hooks/useLeads";
import { TrendingUp, Download, Filter, CalendarIcon, BarChart3, PieChart as PieChartIcon } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { cn } from "@/lib/utils";
import { Lead } from "@/types";
import { exportDisbursements } from "@/services/excelExportService";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface DisbursementAnalyticsProps {
  teamMemberFilter: string;
  currentMonthOnly?: boolean;
}

export function DisbursementAnalytics({ 
  teamMemberFilter, 
  currentMonthOnly = false 
}: DisbursementAnalyticsProps) {
  const { leads } = useLeads();
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  
  // Initialize dates based on currentMonthOnly prop
  const now = new Date();
  const initialStartDate = currentMonthOnly ? startOfMonth(now) : undefined;
  const initialEndDate = currentMonthOnly ? endOfMonth(now) : undefined;
  
  const [filters, setFilters] = useState({
    dateType: "disbursement" as "consideration" | "disbursement",
    startDate: initialStartDate,
    endDate: initialEndDate,
    bank: "all",
    status: "disbursed",
  });

  // Filter leads when filters change or teamMemberFilter changes
  useEffect(() => {
    let filtered = leads.filter(lead => lead.stage === "disbursed");

    // Apply date filter
    if (filters.startDate || filters.endDate) {
      filtered = filtered.filter(lead => {
        const dateField = filters.dateType === "disbursement" 
          ? lead.disbursementDate 
          : lead.considerationDate;
        
        if (!dateField) return false;
        
        const leadDate = dateField instanceof Date ? dateField : new Date(dateField);
        
        if (filters.startDate && leadDate < filters.startDate) return false;
        if (filters.endDate && leadDate > filters.endDate) return false;
        
        return true;
      });
    }

    // Apply bank filter
    if (filters.bank !== "all") {
      filtered = filtered.filter(lead => lead.bank === filters.bank);
    }

    // Apply team member filter from props
    if (teamMemberFilter !== "all") {
      filtered = filtered.filter(lead => 
        lead.assignedTo === teamMemberFilter || 
        (lead as any).createdBy === teamMemberFilter
      );
    }

    setFilteredLeads(filtered);
  }, [leads, filters, teamMemberFilter]);

  const handleExport = () => {
    if (filteredLeads.length === 0) {
      toast.error("No data to export");
      return;
    }
    try {
      exportDisbursements(filteredLeads);
      toast.success(`Exported ${filteredLeads.length} disbursement records to Excel`);
    } catch (error) {
      toast.error("Failed to export data");
      console.error("Export error:", error);
    }
  };

  const totalDisbursement = filteredLeads.reduce((sum, lead) => {
    return sum + (lead.disbursedAmount || lead.sanctionedAmount || 0);
  }, 0);

  const avgDisbursement = filteredLeads.length > 0 
    ? totalDisbursement / filteredLeads.length 
    : 0;

  // Get unique banks from leads
  const uniqueBanks = Array.from(new Set(leads.map(l => l.bank).filter(Boolean)));

  // Prepare chart data
  const bankData = uniqueBanks.map(bank => {
    const bankLeads = filteredLeads.filter(l => l.bank === bank);
    const total = bankLeads.reduce((sum, lead) => 
      sum + (lead.disbursedAmount || lead.sanctionedAmount || 0), 0
    );
    return {
      bank,
      count: bankLeads.length,
      amount: total / 100000, // Convert to lakhs
    };
  }).sort((a, b) => b.amount - a.amount);

  // Status distribution (for demo purposes, since all are disbursed)
  const statusData = [
    { name: "Disbursed", value: filteredLeads.length, color: "#22c55e" },
  ];

  // Monthly trend data (if date range is available)
  const getTrendData = () => {
    if (!filters.startDate || !filters.endDate) return [];
    
    const monthlyMap = new Map<string, number>();
    filteredLeads.forEach(lead => {
      const date = lead.disbursementDate 
        ? (lead.disbursementDate instanceof Date ? lead.disbursementDate : new Date(lead.disbursementDate))
        : null;
      
      if (date) {
        const monthKey = format(date, "MMM yyyy");
        const amount = (lead.disbursedAmount || lead.sanctionedAmount || 0) / 100000;
        monthlyMap.set(monthKey, (monthlyMap.get(monthKey) || 0) + amount);
      }
    });

    return Array.from(monthlyMap.entries())
      .map(([month, amount]) => ({ month, amount }))
      .slice(0, 12); // Limit to 12 months
  };

  const trendData = getTrendData();

  const CHART_COLORS = ["#22c55e", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Disbursement Analytics
            </CardTitle>
            {currentMonthOnly && (
              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                <CalendarIcon className="h-3 w-3" />
                Showing current month data only
              </p>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export to Excel
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Filters */}
        <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
          <div className="flex items-center gap-2 mb-2">
            <Filter className="h-4 w-4" />
            <h3 className="font-medium">Filters</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-2">
              <Label className="text-sm">Date Type</Label>
              <Select 
                value={filters.dateType} 
                onValueChange={(v: "consideration" | "disbursement") => 
                  setFilters(p => ({ ...p, dateType: v }))
                }
              >
                <SelectTrigger className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="consideration">Consideration Date</SelectItem>
                  <SelectItem value="disbursement">Disbursement Date</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={currentMonthOnly}
                    className={cn(
                      "h-10 w-full justify-start text-left font-normal",
                      !filters.startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.startDate ? format(filters.startDate, "PPP") : "Pick date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={filters.startDate}
                    onSelect={(date) => setFilters(p => ({ ...p, startDate: date }))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={currentMonthOnly}
                    className={cn(
                      "h-10 w-full justify-start text-left font-normal",
                      !filters.endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.endDate ? format(filters.endDate, "PPP") : "Pick date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={filters.endDate}
                    onSelect={(date) => setFilters(p => ({ ...p, endDate: date }))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Bank</Label>
              <Select 
                value={filters.bank} 
                onValueChange={(v) => setFilters(p => ({ ...p, bank: v }))}
              >
                <SelectTrigger className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Banks</SelectItem>
                  {uniqueBanks.map((bank) => (
                    <SelectItem key={bank} value={bank || ""}>{bank}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setFilters({
                dateType: "disbursement",
                startDate: undefined,
                endDate: undefined,
                bank: "all",
                status: "disbursed",
                teamMember: "all",
              })}
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Total Cases</p>
                <p className="text-3xl font-bold">{filteredLeads.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Total Disbursement</p>
                <p className="text-3xl font-bold">₹{(totalDisbursement / 10000000).toFixed(2)}Cr</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Avg Disbursement</p>
                <p className="text-3xl font-bold">₹{(avgDisbursement / 100000).toFixed(2)}L</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        {filteredLeads.length > 0 && (
          <Tabs defaultValue="bank" className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-3">
              <TabsTrigger value="bank">
                <BarChart3 className="h-4 w-4 mr-2" />
                By Bank
              </TabsTrigger>
              <TabsTrigger value="trend">
                <TrendingUp className="h-4 w-4 mr-2" />
                Trend
              </TabsTrigger>
              <TabsTrigger value="status">
                <PieChartIcon className="h-4 w-4 mr-2" />
                Status
              </TabsTrigger>
            </TabsList>

            <TabsContent value="bank" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Disbursement by Bank</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={bankData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="bank" angle={-45} textAnchor="end" height={80} />
                      <YAxis />
                      <Tooltip 
                        formatter={(value: number) => [`₹${value.toFixed(2)}L`, "Amount"]}
                        labelFormatter={(label) => `Bank: ${label}`}
                      />
                      <Legend />
                      <Bar dataKey="amount" fill="#22c55e" name="Disbursement (₹L)" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="trend" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Monthly Disbursement Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  {trendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip 
                          formatter={(value: number) => [`₹${value.toFixed(2)}L`, "Amount"]}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="amount" 
                          stroke="#22c55e" 
                          strokeWidth={2}
                          name="Disbursement (₹L)"
                          dot={{ fill: "#22c55e", r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      <p>Select a date range to view trend data</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="status" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Status Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${value}`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* Data Table */}
        <div className="space-y-2">
          <h3 className="font-medium">Disbursed Cases</h3>
          <div className="border rounded-lg overflow-hidden">
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium">Customer</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Bank</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Amount</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Date</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                        No disbursed cases found matching the filters
                      </td>
                    </tr>
                  ) : (
                    filteredLeads.map((lead) => (
                      <tr key={lead.id} className="border-t hover:bg-muted/50">
                        <td className="px-4 py-3 text-sm">{lead.customerName}</td>
                        <td className="px-4 py-3 text-sm">{lead.bank}</td>
                        <td className="px-4 py-3 text-sm font-medium">
                          ₹{((lead.disbursedAmount || lead.sanctionedAmount || 0) / 100000).toFixed(2)}L
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {lead.disbursementDate 
                            ? format(lead.disbursementDate instanceof Date ? lead.disbursementDate : new Date(lead.disbursementDate), "dd MMM yyyy")
                            : "-"}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant="default">Disbursed</Badge>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
